import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { logout } from '../../store/actions/adminAuthActions';
import { useLanguage } from '../../contexts/LanguageContext';
import {
    FiHome,
    FiDollarSign,
    FiAlertTriangle,
    FiUser,
    FiFileText,
    FiMail,
    FiMessageSquare,
    FiUsers,
    FiNavigation,
    FiCalendar,
    FiCloudRain,
    FiCloudOff,
    FiCreditCard,
    FiBriefcase,
    FiBarChart2,
    FiChevronDown,
    FiChevronRight,
    FiChevronLeft,
    FiCheckCircle,
    FiHelpCircle,
    FiShield,
    FiEye,
    FiLogOut,
    FiMenu,
    FiX,
    FiActivity,
    FiGift,
} from 'react-icons/fi';
import { ORAKLE_WEATHER_WEBSITE } from '../../constants/colors';
import { useTranslation } from '../../hooks/useTranslation';
import LanguageSwitch from '../Common/LanguageSwitch';
import './DashboardLayout.css';
import '../../styles/admin-design-system.css';
import '../../styles/orakle-button.css';

const DashboardLayout = () => {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState({});
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { firstName, lastName, email } = useSelector((state) => state.adminAuth);

    const userInitials = [firstName, lastName]
        .filter(Boolean)
        .map((n) => n.charAt(0).toUpperCase())
        .join('') || 'OW';

    const userDisplayName = [firstName, lastName].filter(Boolean).join(' ') || 'Administrateur';
    const { language, setLanguage } = useLanguage();
    const { t } = useTranslation();

    const handleLogout = () => {
        dispatch(logout());
        navigate('/admin/login');
    };

    const isActive = (path) => {
        if (path === '/dashboard/operations') {
            return location.pathname.startsWith('/dashboard/operations');
        }
        if (path === '/admin/trips/commercial-gestures') {
            return location.pathname.startsWith('/admin/trips/commercial-gestures');
        }
        return location.pathname === path;
    };

    // Déterminer quels groupes doivent être ouverts par défaut basé sur la route actuelle
    useEffect(() => {
        const path = location.pathname;
        const newExpandedGroups = {};

        if (path.startsWith('/dashboard/operations')) {
            newExpandedGroups['Operations'] = true;
        }

        if (path.startsWith('/admin/partner') || path.startsWith('/admin/reference') ||
            path.startsWith('/admin/email') || path.startsWith('/admin/sms') || path.startsWith('/admin/mailing-list') || path.startsWith('/admin/management') || path.startsWith('/admin/client')) {
            newExpandedGroups['Admin'] = true;
        }

        if (path.startsWith('/admin/trips') || path.startsWith('/trip/') || path.startsWith('/admin/weather-daily-analysis')) {
            newExpandedGroups['Trip'] = true;
        }

        if (path.startsWith('/activity/')) {
            newExpandedGroups['Activity'] = true;
        }

        setExpandedGroups(prev => ({ ...prev, ...newExpandedGroups }));
    }, [location.pathname]);

    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    const hasActiveDescendant = (item) => {
        if (item.path && isActive(item.path)) return true;
        if (!item.children) return false;
        return item.children.some((child) => hasActiveDescendant(child));
    };

    // Create menu items with translations and preserve original labels for group tracking
    const menuItems = [
        {
            label: t('sidebar.welcome'),
            originalLabel: 'Welcome',
            path: '/welcome',
            icon: FiHome,
        },
        {
            label: t('sidebar.operations'),
            originalLabel: 'Operations',
            path: '/dashboard/operations',
            icon: FiActivity,
        },


        {
            label: t('sidebar.admin'),
            originalLabel: 'Admin',
            icon: FiUser,
            children: [
                { label: t('sidebar.adminMenu.partner'), path: '/admin/partner', icon: FiBriefcase },
                { label: t('sidebar.adminMenu.references'), path: '/admin/reference', icon: FiFileText },
                { label: t('sidebar.adminMenu.emailManagement'), path: '/admin/email', icon: FiMail },
                { label: t('sidebar.adminMenu.smsManagement'), path: '/admin/sms', icon: FiMessageSquare },
                { label: t('sidebar.adminMenu.adminManagement'), path: '/admin/management', icon: FiMail },
                { label: t('sidebar.adminMenu.client'), path: '/admin/client', icon: FiUsers },
            ],
        },
        {
            label: t('sidebar.trip'),
            originalLabel: 'Trip',
            icon: FiNavigation,
            children: [
                {
                    label: t('sidebar.weatherDailyAnalysis'),
                    path: '/admin/weather-daily-analysis',
                    icon: FiCloudRain,
                },
                { label: t('sidebar.tripMenu.dailyManagement'), path: '/admin/trips/daily', icon: FiCalendar },
                { label: t('sidebar.tripMenu.staysManagement'), path: '/admin/trips', icon: FiNavigation },
                { label: t('sidebar.tripMenu.policiesManagement'), path: '/admin/trips/policies', icon: FiCreditCard },
                { label: t('sidebar.tripMenu.commercialGestures'), path: '/admin/trips/commercial-gestures', icon: FiGift },
                { label: t('sidebar.tripMenu.claimsManagement'), path: '/admin/trips/claims', icon: FiAlertTriangle },
                { label: t('sidebar.tripMenu.forecastBackupCheck'), path: '/admin/trips/forecast-backup-check', icon: FiShield },
                { label: t('sidebar.tripMenu.observationBackupCheck'), path: '/admin/trips/observation-backup-check', icon: FiEye },
                { label: t('sidebar.tripMenu.checkClaims'), path: '/admin/trips/check-claims', icon: FiCheckCircle },
                { label: t('sidebar.tripMenu.forecastsManagement'), path: '/admin/trips/forecasts', icon: FiCloudRain },
                { label: t('sidebar.tripMenu.observationsManagement'), path: '/admin/trips/observations', icon: FiBarChart2 },
                { label: t('sidebar.tripMenu.forecastsNotTriggered'), path: '/admin/trips/forecasts-not-triggered', icon: FiCloudRain },
            ],
        },
        {
            label: t('sidebar.activity'),
            originalLabel: 'Activity',
            icon: FiBriefcase,
            children: [
                //{ label: t('sidebar.activityMenu.dailyManagement'), path: '/activity/daily', icon: FiCalendar },
                { label: t('sidebar.activityMenu.claimsManagement'), path: '/activity/claims', icon: FiCloudRain },
                { label: t('sidebar.activityMenu.policiesManagement'), path: '/activity/policies', icon: FiCreditCard },
                { label: t('sidebar.activityMenu.forecastsManagement'), path: '/activity/forecasts', icon: FiCloudRain },
                { label: t('sidebar.activityMenu.forecastsNotTriggered'), path: '/activity/forecasts-not-triggered', icon: FiCloudOff },
                { label: t('sidebar.activityMenu.observationsManagement'), path: '/activity/observations', icon: FiBarChart2 },
                { label: t('sidebar.activityMenu.activitiesManagement'), path: '/activity/activities', icon: FiCalendar },
                { label: t('sidebar.activityMenu.quotesManagement'), path: '/activity/quotes', icon: FiFileText },
            ],
        },

    ];

    const renderMenuItem = (item, level = 0) => {
        const Icon = item.icon;
        const groupKey = item.originalLabel || item.label;

        if (item.children) {
            const isExpanded = expandedGroups[groupKey] || false;
            const hasActiveChild = hasActiveDescendant(item);

            return (
                <div key={groupKey} className={`menu-group ${level > 0 ? `submenu submenu-level-${level}` : ''}`}>
                    <div
                        className={`menu-group-header ${hasActiveChild ? 'has-active' : ''}`}
                        onClick={() => toggleGroup(groupKey)}
                    >
                        {Icon && <Icon className="menu-icon" />}
                        <span className="menu-label">{item.label}</span>
                        <span className="menu-chevron">
                            {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                        </span>
                    </div>
                    <div className={`menu-group-children ${isExpanded ? 'expanded' : 'collapsed'}`}>
                        {item.children.length > 0
                            ? item.children.map((child) => renderMenuItem(child, level + 1))
                            : (
                                <div className="menu-item menu-item--placeholder" key={`${groupKey}-empty`}>
                                    <span className="menu-label">
                                        {groupKey === 'WeatherObservations'
                                            ? t('sidebar.weatherDailyAnalysisMenu.observationsPlaceholder')
                                            : t('sidebar.weatherDailyAnalysisMenu.specificPlaceholder')}
                                    </span>
                                </div>
                            )}
                    </div>
                </div>
            );
        }

        return (
            <div
                key={item.path}
                className={`menu-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => navigate(item.path)}
            >
                {Icon && <Icon className="menu-icon" />}
                <span className="menu-label">{item.label}</span>
            </div>
        );
    };


    return (
        <div className="dashboard-layout">
            {/* Sidebar */}
            <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-header">
                    <a href={ORAKLE_WEATHER_WEBSITE} target="_blank" rel="noopener noreferrer" className="brand-link">
                        <div className="brand-logo-wrap">
                            <img
                                src="https://logo-orakle.s3.eu-west-3.amazonaws.com/logo_fond_bleu+(3).png"
                                alt="Orakle Weather Logo"
                                className="brand-logo-img"
                            />
                        </div>
                        {!sidebarCollapsed && (
                            <div className="brand-title-wrapper">
                                <div className="brand-title-line">Orakle Weather</div>
                                <div className="brand-subtitle">Admin Platform</div>
                            </div>
                        )}
                    </a>
                    {!sidebarCollapsed && (
                        <button
                            className="sidebar-toggle-btn"
                            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                            aria-label="Collapse sidebar"
                            title="Réduire la sidebar"
                        >
                            <FiChevronLeft />
                        </button>
                    )}
                </div>

                <nav className="sidebar-nav">
                    {menuItems.map(item => renderMenuItem(item))}
                </nav>

                {!sidebarCollapsed && (
                    <div className="sidebar-footer">
                        <div className="sidebar-user-card">
                            <div className="sidebar-user-avatar" aria-hidden="true">{userInitials}</div>
                            <div className="sidebar-user-info">
                                <span className="sidebar-user-name">{userDisplayName}</span>
                                {email && <span className="sidebar-user-email">{email}</span>}
                            </div>
                        </div>
                        <div className="sidebar-footer-actions">
                            <div className="menu-item sidebar-footer-item" onClick={() => navigate('/admin/help')}>
                                <FiHelpCircle className="menu-icon" />
                                <span className="menu-label">{t('sidebar.footer.help')}</span>
                            </div>
                            <div className="menu-item sidebar-footer-item sidebar-footer-item--logout" onClick={handleLogout}>
                                <FiLogOut className="menu-icon" />
                                <span className="menu-label">{t('sidebar.footer.logout')}</span>
                            </div>
                        </div>
                        <div className="sidebar-version">v1.0.0</div>
                    </div>
                )}
            </aside>

            {/* Main Content */}
            <div className="main-content">
                {/* Header - BuyNavigation Style */}
                <nav className={`admin-topbar${sidebarCollapsed ? ' admin-topbar--collapsed' : ''}`}>
                    <div className="admin-topbar__inner">
                        <div className="admin-topbar__row">
                            <div className="admin-topbar__left">
                                {sidebarCollapsed && (
                                    <button
                                        type="button"
                                        className="admin-topbar__expand-btn"
                                        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                                        aria-label="Expand sidebar"
                                        title="Agrandir la sidebar"
                                    >
                                        <FiChevronRight />
                                    </button>
                                )}
                            </div>
                            <div className="admin-topbar__right">
                                <div className="admin-topbar__lang">
                                    <LanguageSwitch />
                                </div>
                                <div className="admin-topbar__divider" aria-hidden="true" />
                                <div className="admin-topbar__user">
                                    <div className="admin-topbar__avatar" aria-hidden="true">{userInitials}</div>
                                    <div className="admin-topbar__user-text">
                                        <span className="admin-topbar__user-name">{userDisplayName}</span>
                                        {email && <span className="admin-topbar__user-email">{email}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </nav>

                {/* Page Content */}
                <main className="page-content">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default DashboardLayout;

